/**
 * Phase 10 R5 — computeFinancialWarnings
 *
 * Pure derive helper. Turns a TicketFinancialSummary + shop targets into a
 * sorted list of warnings.
 *
 * STRICT TWO-CLASS MODEL (per R5 review tightening #1):
 *   `target_*_gp_percent` and `low_margin_warn_threshold` produce DISTINCT
 *   warning classes that may fire together by design:
 *
 *     • Below-target  (`*_below_target`, severity=warn)
 *         Operational goal miss — "we wanted 60%, got 45%"
 *     • Low-margin floor (`total_low_margin`, severity=warn)
 *         Severity floor — "regardless of goals, this is dangerously thin"
 *     • Negative GP   (`negative_gp`, severity=critical)
 *         Hard severity — "this ticket loses money"
 *
 *   Concrete example: target_total=25%, lowMargin=10%, ticket at 8% →
 *   fires BOTH `total_below_target` AND `total_low_margin`. This is
 *   intentional — they answer different questions and neither suppresses
 *   the other.
 *
 *   Completeness notes from R4 are folded in as `info`-severity items so
 *   the UI has a single warning stack.
 *
 * SORT ORDER (UI consumes as-is):
 *   critical → warn-floor → warn-target → info-completeness
 */
import type {
  CompletenessStatus,
  TicketFinancialSummary,
} from './computeTicketFinancialSummary';

export type WarningSeverity = 'info' | 'warn' | 'critical';

export type WarningCode =
  | 'parts_below_target'
  | 'labor_below_target'
  | 'total_below_target'
  | 'total_low_margin'
  | 'negative_gp'
  | 'completeness_note';

export interface FinancialWarning {
  severity: WarningSeverity;
  code: WarningCode;
  message: string;
}

export interface ShopFinancialTargets {
  parts: number | null;
  labor: number | null;
  total: number | null;
  /** Severity floor in percent — defaults to 10. Always present. */
  lowMargin: number;
}

/** Sort priority (lower = shown first in UI). */
const ORDER: Record<WarningCode, number> = {
  negative_gp: 0,
  total_low_margin: 1,
  total_below_target: 2,
  parts_below_target: 3,
  labor_below_target: 4,
  completeness_note: 5,
};

const r1 = (n: number) => Math.round(n * 10) / 10;

export function computeFinancialWarnings(
  summary: TicketFinancialSummary,
  targets: ShopFinancialTargets,
): FinancialWarning[] {
  const warnings: FinancialWarning[] = [];
  const { parts, labor, combined, completeness } = summary;

  // 1. Critical — negative combined GP.
  if (combined.gpAmount < 0) {
    warnings.push({
      severity: 'critical',
      code: 'negative_gp',
      message: `Negative gross profit: $${combined.gpAmount.toFixed(2)}`,
    });
  }

  // 2. Severity floor — low total margin (independent from goals).
  //    Only meaningful when there's any sell on the ticket.
  if (
    combined.sell > 0 &&
    combined.gpAmount >= 0 && // negative_gp covers the worse case already
    combined.gpPercent < targets.lowMargin
  ) {
    warnings.push({
      severity: 'warn',
      code: 'total_low_margin',
      message: `Total margin is dangerously low (${r1(combined.gpPercent)}% < ${r1(targets.lowMargin)}% floor)`,
    });
  }

  // 3. Below-target (operational goal misses). Each fires independently.
  if (
    targets.total != null &&
    combined.sell > 0 &&
    combined.gpPercent < targets.total
  ) {
    warnings.push({
      severity: 'warn',
      code: 'total_below_target',
      message: `Total GP below target (${r1(combined.gpPercent)}% vs ${r1(targets.total)}%)`,
    });
  }
  if (
    targets.parts != null &&
    parts.sell > 0 &&
    parts.gpPercent < targets.parts
  ) {
    warnings.push({
      severity: 'warn',
      code: 'parts_below_target',
      message: `Parts GP below target (${r1(parts.gpPercent)}% vs ${r1(targets.parts)}%)`,
    });
  }
  if (
    targets.labor != null &&
    labor.sell > 0 &&
    labor.gpPercent < targets.labor
  ) {
    warnings.push({
      severity: 'warn',
      code: 'labor_below_target',
      message: `Labor GP below target (${r1(labor.gpPercent)}% vs ${r1(targets.labor)}%)`,
    });
  }

  // 4. Info — completeness notes from R4 (preserved 1:1).
  for (const note of completeness.notes) {
    warnings.push({ severity: 'info', code: 'completeness_note', message: note });
  }

  // Sort: by code-priority, then severity tiebreaker (critical > warn > info)
  // already implied by the priority map.
  warnings.sort((a, b) => ORDER[a.code] - ORDER[b.code]);
  return warnings;
}

/** Convenience: roll up a worst-of severity for a list of warnings. */
export function worstSeverity(
  warnings: FinancialWarning[],
): WarningSeverity | null {
  if (warnings.some((w) => w.severity === 'critical')) return 'critical';
  if (warnings.some((w) => w.severity === 'warn')) return 'warn';
  if (warnings.some((w) => w.severity === 'info')) return 'info';
  return null;
}

/** Helper for callers that just want a count of "needs attention" tickets. */
export function isActionable(severity: WarningSeverity | null): boolean {
  return severity === 'critical' || severity === 'warn';
}

/** Helper to coerce CompletenessStatus to a UI tone. */
export function toneForCompleteness(s: CompletenessStatus): WarningSeverity | null {
  if (s === 'insufficient') return 'warn';
  if (s === 'partial') return 'info';
  return null;
}
